import { RefObject, useEffect, useRef, useState } from 'react';
import { FabricObject, StaticCanvas } from 'fabric';

interface UseFabricCanvasProps {
  canvasRef: RefObject<HTMLCanvasElement>;
  jsonData: { version: string; objects: FabricObject[] };
}

const useFabricCanvas = ({ canvasRef, jsonData }: UseFabricCanvasProps) => {
  const [canvas, setCanvas] = useState<StaticCanvas | null>(null);
  const initializedRef = useRef(false);

  const calculateBoundingBox = (canvas: StaticCanvas) => {
    const objects = canvas.getObjects();

    if (objects.length === 0) {
      return { left: 0, top: 0, width: 0, height: 0 };
    }

    const firstObject = objects[0].getBoundingRect();
    let left = firstObject.left;
    let top = firstObject.top;
    let right = firstObject.left + firstObject.width;
    let bottom = firstObject.top + firstObject.height;

    objects.forEach((obj: FabricObject) => {
      const boundingRect = obj.getBoundingRect();
      left = Math.min(left, boundingRect.left);
      top = Math.min(top, boundingRect.top);
      right = Math.max(right, boundingRect.left + boundingRect.width);
      bottom = Math.max(bottom, boundingRect.top + boundingRect.height);
    });

    return {
      left,
      top,
      width: right - left,
      height: bottom - top,
    };
  };

  const scaleToFitCanvas = () => {
    if (!canvas || !jsonData) return;

    const canvasWidth = canvas.getWidth();
    const canvasHeight = canvas.getHeight();

    const boundingBox = calculateBoundingBox(canvas);

    if (boundingBox.width === 0 || boundingBox.height === 0) return;

    const scaleX = canvasWidth / boundingBox.width;
    const scaleY = canvasHeight / boundingBox.height;
    const scale = Math.min(scaleX, scaleY);

    const objects = canvas.getObjects();
    objects.forEach((obj) => {
      obj.scaleX = (obj.scaleX || 1) * scale;
      obj.scaleY = (obj.scaleY || 1) * scale;
      obj.left = ((obj.left || 0) - boundingBox.left) * scale;
      obj.top = ((obj.top || 0) - boundingBox.top) * scale;
      obj.setCoords();
    });

    canvas.setDimensions({
      width: boundingBox.width * scale,
      height: boundingBox.height * scale,
    });
  };

  const loadFromJSON = async () => {
    if (canvas && jsonData) {
      await canvas.loadFromJSON(jsonData);
      scaleToFitCanvas();
      canvas.requestRenderAll();
    }
  };

  useEffect(() => {
    if (canvasRef.current && !canvas && !initializedRef.current) {
      const newCanvas = new StaticCanvas(canvasRef.current);
      setCanvas(newCanvas);
      initializedRef.current = true;
    }

    return () => {
      if (canvas) {
        canvas.dispose();
        setCanvas(null);
        initializedRef.current = false;
      }
    };
  }, [canvas]);

  useEffect(() => {
    loadFromJSON();
  }, [canvas, jsonData]);

  return { canvas };
};

export default useFabricCanvas;
